import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlayCircle, BookOpen, Video, Lock, Plus, Edit2, Trash2, X, Save } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useSupabaseTutorials, useSupabaseTutorialCategories, Tutorial, TutorialCategory } from '../hooks/useSupabaseTutorials';

export default function TutorialsPage() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as 'en' | 'de' | 'cz' | 'sk';
  const location = useLocation();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showTutorialModal, setShowTutorialModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingTutorial, setEditingTutorial] = useState<Tutorial | null>(null);
  const [editingCategory, setEditingCategory] = useState<TutorialCategory | null>(null);

  const isAdmin = new URLSearchParams(location.search).get('admin') === 'true' ||
                  localStorage.getItem('admin_logged_in') === 'true';

  const { categories, loading: categoriesLoading, addCategory, updateCategory, deleteCategory } = useSupabaseTutorialCategories();
  const { tutorials, loading: tutorialsLoading, addTutorial, updateTutorial, deleteTutorial } = useSupabaseTutorials(selectedCategory || undefined);

  const getVideoId = (url: string) => {
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(youtubeRegex);
    return match ? match[1] : null;
  };

  const filteredTutorials = selectedCategory
    ? tutorials.filter(t => t.category_id === selectedCategory)
    : tutorials;

  const handleAddTutorial = () => {
    setEditingTutorial(null);
    setShowTutorialModal(true);
  };

  const handleEditTutorial = (tutorial: Tutorial) => {
    setEditingTutorial(tutorial);
    setShowTutorialModal(true);
  };

  const handleDeleteTutorial = async (id: string) => {
    if (window.confirm(t('tutorials.confirmDelete', 'Opravdu chcete smazat tento návod?'))) {
      try {
        await deleteTutorial(id);
      } catch (error) {
        console.error('Error deleting tutorial:', error);
        alert(t('tutorials.deleteError', 'Chyba při mazání návodu'));
      }
    }
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category: TutorialCategory) => {
    setEditingCategory(category);
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = async (id: string) => {
    if (window.confirm(t('tutorials.confirmDeleteCategory', 'Opravdu chcete smazat tuto kategorii?'))) {
      try {
        await deleteCategory(id);
      } catch (error) {
        console.error('Error deleting category:', error);
        alert(t('tutorials.deleteCategoryError', 'Chyba při mazání kategorie'));
      }
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-16">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <BookOpen className="w-10 h-10 text-cyan-400" />
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              {t('tutorials.title', 'Video Tutorials')}
            </h1>
          </div>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            {t('tutorials.subtitle', 'Learn how to use our products with step-by-step video guides')}
          </p>

          {isAdmin && (
            <div className="flex justify-center gap-4 mt-6">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleAddTutorial}
                className="flex items-center gap-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-6 py-3 rounded-lg font-semibold shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all"
              >
                <Plus className="w-5 h-5" />
                {t('tutorials.addTutorial', 'Přidat návod')}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleAddCategory}
                className="flex items-center gap-2 bg-slate-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-slate-600 transition-all"
              >
                <Plus className="w-5 h-5" />
                {t('tutorials.addCategory', 'Přidat kategorii')}
              </motion.button>
            </div>
          )}
        </motion.div>

        {categoriesLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto"></div>
            <p className="text-slate-400 mt-4">{t('loading', 'Načítám...')}</p>
          </div>
        ) : (
          <>
            {/* Category Filter */}
            <div className="mb-8 flex flex-wrap gap-3 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedCategory(null)}
                className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
                  selectedCategory === null
                    ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-lg shadow-cyan-500/25'
                    : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50 border border-slate-700'
                }`}
              >
                {t('all', 'All')}
              </motion.button>
              {categories.map((category) => (
                <div key={category.id} className="relative group">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
                      selectedCategory === category.id
                        ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-lg shadow-cyan-500/25'
                        : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50 border border-slate-700'
                    }`}
                  >
                    {category.name[currentLang] || category.name.cz}
                  </motion.button>
                  {isAdmin && (
                    <div className="absolute -top-2 -right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditCategory(category);
                        }}
                        className="bg-blue-600 hover:bg-blue-500 text-white p-1 rounded"
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCategory(category.id);
                        }}
                        className="bg-red-600 hover:bg-red-500 text-white p-1 rounded"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Tutorials Grid */}
            {tutorialsLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto"></div>
                <p className="text-slate-400 mt-4">{t('loading', 'Načítám...')}</p>
              </div>
            ) : filteredTutorials.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-16"
              >
                <Video className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-lg">
                  {t('tutorials.noTutorials', 'No tutorials available in this category yet.')}
                </p>
              </motion.div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredTutorials.map((tutorial, index) => {
                  const videoId = getVideoId(tutorial.video_url);
                  const thumbnailUrl = tutorial.thumbnail_url ||
                    (videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null);

                  return (
                    <motion.div
                      key={tutorial.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="group relative bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-xl overflow-hidden border border-slate-700/50 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/10"
                    >
                      {/* Thumbnail */}
                      <div className="relative aspect-video bg-slate-900 overflow-hidden">
                        {thumbnailUrl ? (
                          <img
                            src={thumbnailUrl}
                            alt={tutorial.title[currentLang] || tutorial.title.cz}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Video className="w-12 h-12 text-slate-700" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <motion.div
                            whileHover={{ scale: 1.1 }}
                            className="bg-cyan-500/90 rounded-full p-4"
                          >
                            <PlayCircle className="w-12 h-12 text-white" />
                          </motion.div>
                        </div>
                        {!tutorial.published && (
                          <div className="absolute top-3 right-3 bg-yellow-500/90 px-3 py-1 rounded-lg flex items-center gap-1.5">
                            <Lock className="w-3.5 h-3.5" />
                            <span className="text-xs font-semibold">Draft</span>
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-5">
                        <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2 group-hover:text-cyan-400 transition-colors">
                          {tutorial.title[currentLang] || tutorial.title.cz}
                        </h3>
                        <p className="text-slate-400 text-sm line-clamp-3 mb-4">
                          {tutorial.description[currentLang] || tutorial.description.cz}
                        </p>
                        <div className="flex items-center justify-between">
                          <a
                            href={tutorial.video_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-medium text-sm group/link"
                          >
                            <PlayCircle className="w-4 h-4 group-hover/link:scale-110 transition-transform" />
                            {t('tutorials.watchVideo', 'Watch Tutorial')}
                          </a>
                          {isAdmin && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditTutorial(tutorial)}
                                className="p-2 bg-blue-600/80 hover:bg-blue-500 rounded text-white transition-colors"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteTutorial(tutorial.id)}
                                className="p-2 bg-red-600/80 hover:bg-red-500 rounded text-white transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Tutorial Modal */}
      <AnimatePresence>
        {showTutorialModal && (
          <TutorialModal
            tutorial={editingTutorial}
            categories={categories}
            onClose={() => setShowTutorialModal(false)}
            onSave={async (tutorialData) => {
              try {
                if (editingTutorial) {
                  await updateTutorial(editingTutorial.id, tutorialData);
                } else {
                  await addTutorial(tutorialData as any);
                }
                setShowTutorialModal(false);
              } catch (error) {
                console.error('Error saving tutorial:', error);
                alert(t('tutorials.saveError', 'Chyba při ukládání návodu'));
              }
            }}
          />
        )}
      </AnimatePresence>

      {/* Category Modal */}
      <AnimatePresence>
        {showCategoryModal && (
          <CategoryModal
            category={editingCategory}
            onClose={() => setShowCategoryModal(false)}
            onSave={async (categoryData) => {
              try {
                if (editingCategory) {
                  await updateCategory(editingCategory.id, categoryData);
                } else {
                  await addCategory(categoryData as any);
                }
                setShowCategoryModal(false);
              } catch (error) {
                console.error('Error saving category:', error);
                alert(t('tutorials.saveCategoryError', 'Chyba při ukládání kategorie'));
              }
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Tutorial Modal Component
function TutorialModal({
  tutorial,
  categories,
  onClose,
  onSave,
}: {
  tutorial: Tutorial | null;
  categories: TutorialCategory[];
  onClose: () => void;
  onSave: (data: Partial<Tutorial>) => void;
}) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    category_id: tutorial?.category_id || categories[0]?.id || '',
    title_en: tutorial?.title.en || '',
    title_de: tutorial?.title.de || '',
    title_cz: tutorial?.title.cz || '',
    title_sk: tutorial?.title.sk || '',
    description_en: tutorial?.description.en || '',
    description_de: tutorial?.description.de || '',
    description_cz: tutorial?.description.cz || '',
    description_sk: tutorial?.description.sk || '',
    video_url: tutorial?.video_url || '',
    thumbnail_url: tutorial?.thumbnail_url || '',
    order: tutorial?.order || 0,
    published: tutorial?.published ?? true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      category_id: formData.category_id,
      title: {
        en: formData.title_en,
        de: formData.title_de,
        cz: formData.title_cz,
        sk: formData.title_sk,
      },
      description: {
        en: formData.description_en,
        de: formData.description_de,
        cz: formData.description_cz,
        sk: formData.description_sk,
      },
      video_url: formData.video_url,
      thumbnail_url: formData.thumbnail_url || undefined,
      order: formData.order,
      published: formData.published,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-slate-900 border border-cyan-500/30 rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">
            {tutorial ? t('tutorials.editTutorial', 'Upravit návod') : t('tutorials.addTutorial', 'Přidat návod')}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-slate-300 mb-2">
              {t('tutorials.category', 'Kategorie')}
            </label>
            <select
              value={formData.category_id}
              onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
              required
            >
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name.cz}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-300 mb-2">
                {t('tutorials.titleCz', 'Název (CZ)')}
              </label>
              <input
                type="text"
                value={formData.title_cz}
                onChange={(e) => setFormData({ ...formData, title_cz: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
                required
              />
            </div>
            <div>
              <label className="block text-slate-300 mb-2">
                {t('tutorials.titleSk', 'Název (SK)')}
              </label>
              <input
                type="text"
                value={formData.title_sk}
                onChange={(e) => setFormData({ ...formData, title_sk: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
                required
              />
            </div>
            <div>
              <label className="block text-slate-300 mb-2">
                {t('tutorials.titleEn', 'Název (EN)')}
              </label>
              <input
                type="text"
                value={formData.title_en}
                onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
                required
              />
            </div>
            <div>
              <label className="block text-slate-300 mb-2">
                {t('tutorials.titleDe', 'Název (DE)')}
              </label>
              <input
                type="text"
                value={formData.title_de}
                onChange={(e) => setFormData({ ...formData, title_de: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-300 mb-2">
                {t('tutorials.descriptionCz', 'Popis (CZ)')}
              </label>
              <textarea
                value={formData.description_cz}
                onChange={(e) => setFormData({ ...formData, description_cz: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
                rows={3}
                required
              />
            </div>
            <div>
              <label className="block text-slate-300 mb-2">
                {t('tutorials.descriptionSk', 'Popis (SK)')}
              </label>
              <textarea
                value={formData.description_sk}
                onChange={(e) => setFormData({ ...formData, description_sk: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
                rows={3}
                required
              />
            </div>
            <div>
              <label className="block text-slate-300 mb-2">
                {t('tutorials.descriptionEn', 'Popis (EN)')}
              </label>
              <textarea
                value={formData.description_en}
                onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
                rows={3}
                required
              />
            </div>
            <div>
              <label className="block text-slate-300 mb-2">
                {t('tutorials.descriptionDe', 'Popis (DE)')}
              </label>
              <textarea
                value={formData.description_de}
                onChange={(e) => setFormData({ ...formData, description_de: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
                rows={3}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-slate-300 mb-2">
              {t('tutorials.videoUrl', 'URL videa (YouTube)')}
            </label>
            <input
              type="url"
              value={formData.video_url}
              onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
              placeholder="https://youtube.com/watch?v=..."
              required
            />
          </div>

          <div>
            <label className="block text-slate-300 mb-2">
              {t('tutorials.thumbnailUrl', 'URL náhledu (volitelné)')}
            </label>
            <input
              type="url"
              value={formData.thumbnail_url}
              onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
              placeholder="https://..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-300 mb-2">
                {t('tutorials.order', 'Pořadí')}
              </label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-slate-300 mt-8">
                <input
                  type="checkbox"
                  checked={formData.published}
                  onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                  className="w-5 h-5"
                />
                {t('tutorials.published', 'Publikováno')}
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              {t('common.cancel', 'Zrušit')}
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {t('common.save', 'Uložit')}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

// Category Modal Component
function CategoryModal({
  category,
  onClose,
  onSave,
}: {
  category: TutorialCategory | null;
  onClose: () => void;
  onSave: (data: Partial<TutorialCategory>) => void;
}) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name_en: category?.name.en || '',
    name_de: category?.name.de || '',
    name_cz: category?.name.cz || '',
    name_sk: category?.name.sk || '',
    order: category?.order || 0,
  });

  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const slug = category?.slug || generateSlug(formData.name_cz);
    onSave({
      name: {
        en: formData.name_en,
        de: formData.name_de,
        cz: formData.name_cz,
        sk: formData.name_sk,
      },
      slug: slug,
      order: formData.order,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-slate-900 border border-cyan-500/30 rounded-2xl p-6 max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">
            {category ? t('tutorials.editCategory', 'Upravit kategorii') : t('tutorials.addCategory', 'Přidat kategorii')}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-300 mb-2">
                {t('tutorials.nameCz', 'Název (CZ)')}
              </label>
              <input
                type="text"
                value={formData.name_cz}
                onChange={(e) => setFormData({ ...formData, name_cz: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
                required
              />
            </div>
            <div>
              <label className="block text-slate-300 mb-2">
                {t('tutorials.nameSk', 'Název (SK)')}
              </label>
              <input
                type="text"
                value={formData.name_sk}
                onChange={(e) => setFormData({ ...formData, name_sk: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
                required
              />
            </div>
            <div>
              <label className="block text-slate-300 mb-2">
                {t('tutorials.nameEn', 'Název (EN)')}
              </label>
              <input
                type="text"
                value={formData.name_en}
                onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
                required
              />
            </div>
            <div>
              <label className="block text-slate-300 mb-2">
                {t('tutorials.nameDe', 'Název (DE)')}
              </label>
              <input
                type="text"
                value={formData.name_de}
                onChange={(e) => setFormData({ ...formData, name_de: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-slate-300 mb-2">
              {t('tutorials.order', 'Pořadí')}
            </label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
            />
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              {t('common.cancel', 'Zrušit')}
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {t('common.save', 'Uložit')}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
